import { useState, useEffect } from "react";
import api from "../../../utils/api";
import {
  formatDateForAPI,
  getLocalDateString,
  normalizeOrderType,
  formatOrderType,
} from "../utils/orderUtils";
import { ORDER_TYPE_ALL } from "../utils/constants";

export const useOrdersHistory = () => {
  const today = new Date();

  const [orders, setOrders] = useState([]);
  const [filtered, setFiltered] = useState([]);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");

  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [showAllDates, setShowAllDates] = useState(false);

  const [orderType, setOrderType] = useState("");
  const [orderTypeOptions, setOrderTypeOptions] = useState([]);

  // Generate order type options
  useEffect(() => {
    const types = Array.from(
      new Set(
        orders.map((o) => normalizeOrderType(o.orderType)).filter(Boolean),
      ),
    );

    const options = [
      ORDER_TYPE_ALL,
      ...types.map((t) => ({
        label: formatOrderType(t),
        value: t,
      })),
    ];

    setOrderTypeOptions(options);
  }, [orders]);

  // Fetch orders when date filters change
  useEffect(() => {
    fetchOrders();
  }, [fromDate, toDate, showAllDates]);

  // Apply filters when any filter changes
  useEffect(() => {
    applyFilters();
  }, [orders, search, status, fromDate, toDate, showAllDates, orderType]);

  const fetchOrders = async () => {
    try {
      let endpoint = "/orders/history";

      if (!showAllDates && fromDate && toDate) {
        endpoint = `/orders/history?startDate=${formatDateForAPI(
          fromDate,
        )}&endDate=${formatDateForAPI(toDate)}`;
      }

      const res = await api.get(endpoint);
      setOrders(res.data || []);
    } catch (err) {
      console.error("Fetch orders error:", err);
      setOrders([]);
    }
  };

  const applyFilters = () => {
    let result = [...orders];

    // DATE FILTER
    if (!showAllDates && fromDate && toDate) {
      const from = formatDateForAPI(fromDate);
      const to = formatDateForAPI(toDate);

      result = result.filter((o) => {
        const d = getLocalDateString(o.createdAt);
        return d >= from && d <= to;
      });
    }

    // STATUS FILTER
    const statusValue =
      typeof status === "object" && status?.value !== undefined
        ? status.value
        : status;
    if (
      statusValue !== "" &&
      statusValue !== null &&
      statusValue !== undefined
    ) {
      result = result.filter(
        (o) => o.status?.toLowerCase() === String(statusValue).toLowerCase(),
      );
    }

    // ORDER TYPE FILTER
    const orderTypeValue =
      typeof orderType === "object" && orderType?.value !== undefined
        ? orderType.value
        : orderType;
    if (
      orderTypeValue !== "" &&
      orderTypeValue !== null &&
      orderTypeValue !== undefined
    ) {
      const normalizedFilter = normalizeOrderType(orderTypeValue);
      result = result.filter((o) => {
        const normalizedOrder = normalizeOrderType(o.orderType);
        return normalizedOrder === normalizedFilter;
      });
    }

    // SEARCH FILTER
    if (search) {
      const s = search.toLowerCase();
      result = result.filter(
        (o) =>
          o.orderId?.toLowerCase().includes(s) ||
          o.phoneNumber?.toLowerCase().includes(s),
      );
    }

    setFiltered(result);
  };

  // Generate aggregated data by order type (date range filter only)
  const getAggregatedData = () => {
    const aggregated = {};

    // Apply only date filter, ignore other filters
    let dataToAggregate = [...orders];

    if (!showAllDates && fromDate && toDate) {
      const from = formatDateForAPI(fromDate);
      const to = formatDateForAPI(toDate);

      dataToAggregate = dataToAggregate.filter((o) => {
        const d = getLocalDateString(o.createdAt);
        return d >= from && d <= to;
      });
    }

    dataToAggregate.forEach((order) => {
      const type = formatOrderType(order.orderType);
      if (!aggregated[type]) {
        aggregated[type] = {
          orderType: type,
          totalOrders: 0,
          totalSales: 0,
        };
      }
      aggregated[type].totalOrders += 1;
      aggregated[type].totalSales += Number(order.amount || 0);
    });

    return Object.values(aggregated);
  };

  return {
    // State
    orders,
    filtered,
    search,
    setSearch,
    status,
    setStatus,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    showAllDates,
    setShowAllDates,
    orderType,
    setOrderType,
    orderTypeOptions,

    // Computed data
    aggregatedData: getAggregatedData(),

    // Actions
    refetchOrders: fetchOrders,
  };
};
